import * as config from "../../config"
import { EmailReceiversFieldsFragment } from "../../graphql-types"

// Map of accepted MIME types to file extensions, used as a fallback when the
// browser provides a File without a usable name/extension (common on Safari /
// iOS when the file comes from the camera or the share sheet).
const MIME_TO_EXTENSION: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/heic": "heic",
  "image/heif": "heif",
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
}

// Guarantees a non-empty file name that keeps an extension. WebKit can expose a
// File with an empty `name`, which strips the extension server-side and causes
// the import to be rejected. We rebuild a safe name from the MIME type.
const buildSafeFileName = (file: File): string => {
  const name = (file.name || "").trim()
  if (name && name.includes(".")) {
    return name
  }
  const extension = MIME_TO_EXTENSION[file.type] || "bin"
  const baseName = name || `upload-${Date.now()}`
  return `${baseName}.${extension}`
}

// The server stores files as `<name>_<timestamp to the second>`: concurrent uploads sharing a name collide and fail with a 500.
const withUniqueSuffix = (fileName: string): string => {
  const suffix = Math.random().toString(36).slice(2, 8)
  const dotIndex = fileName.lastIndexOf(".")
  if (dotIndex <= 0) {
    return `${fileName}-${suffix}`
  }
  return `${fileName.slice(0, dotIndex)}-${suffix}${fileName.slice(dotIndex)}`
}

export const importFiles = async (file: File): Promise<SavedFile> => {
  const headers = { Accept: "application/json", "Ocp-Apim-Subscription-Key": config.notifications_service_token }
  const fileName = withUniqueSuffix(buildSafeFileName(file))

  // Read the File into an in-memory Blob before sending it. WebKit (Safari
  // macOS/iOS) can fail to stream a File lazily read from disk through fetch,
  // which silently breaks the upload. Materialising the bytes first avoids it.
  const fileBytes = await file.arrayBuffer()
  const fileBlob = new Blob([fileBytes], { type: file.type || "application/octet-stream" })

  const formData = new FormData()
  formData.append("file", fileBlob, fileName)

  const response = await fetch(config.notifications_service_import_file_endpoint, {
    method: "POST",
    headers,
    body: formData,
  })

  if (!response.ok) {
    throw new Error(`Failed to import file "${fileName}": ${response.statusText}`)
  }
  return response.json()
}

export const formatReceivers = (receivers: EmailReceiversFieldsFragment[]) => {
  const emailJson = {
    to: "",
    cc: "",
    bcc: "",
  }

  receivers.forEach(receiver => {
    switch (receiver.sendType) {
      case "Principal":
        emailJson.to += receiver.email + ";"
        break
      case "CC":
        emailJson.cc += receiver.email + ";"
        break
      case "CCI":
        emailJson.bcc += receiver.email + ";"
        break
    }
  })

  emailJson.to = emailJson.to.slice(0, -1)
  emailJson.cc = emailJson.cc.slice(0, -1)
  emailJson.bcc = emailJson.bcc.slice(0, -1)

  return emailJson
}
type SendEmailV2Params = {
  trigram: string
  idForm: string
  email?: string
  fields?: { key: string; value: string }[]
  acceptTerms?: string
  header?: string
  footer?: string
  logo?: string
  primaryColor?: string
  locale?: string
  files?: File[]
}
export const sendEmailV2 = async ({
  trigram,
  idForm,
  email,
  fields,
  acceptTerms,
  header,
  footer,
  logo,
  primaryColor,
  locale,
  files = [],
}: SendEmailV2Params): Promise<Response> => {
  const endpoint = config.serverless_sendmail_endpoint
  const ocpmSubscriptionKey = config.serverless_sendmail_token
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Ocp-Apim-Subscription-Key": ocpmSubscriptionKey,
  }

  const body = JSON.stringify({
    trigram: trigram.toUpperCase(),
    idForm,
    email,
    fields,
    acceptTerms,
    header,
    footer,
    logo,
    primaryColor,
    locale,
    attachments: files,
  })

  return fetch(endpoint, {
    method: "POST",
    headers,
    body,
  }).then(response => {
    if (!response.ok) {
      throw new Error(`Erreur API: ${response.statusText}`)
    }
    return response
  })
}

// We don't want to know the type because we're not supposed to do anything with it.
// If that assumption changes, see the OAS of the service (on Keolis.Notifications repository on Azure devops)
type SavedFile = unknown
